// ColorWheelPalette — Clip Studio / Photoshop 風の floating カラーホイールパレット
// (Phase 6.7、consult 2026-05-07 → controlled 化 → 2026-06-20 共有 ColorPanel 化).
//
// ## 2026-06-20: パネル中身を共有 ColorPanel に置換 (consult 2026-06-20)
//
// 確定方針 (consult 2026-06-20):
//   - 「Classic のカラーホイールパネルを **見た目も含めて Modern に寄せていい**。
//      Classic の核は **カラーパレット・ダブルクリックでカラーホイールがポップアップ
//      表示される** ということ」
//   => 保存するのは Classic の **操作流儀** (パレット dblclick → フロート・ポップアップ /
//      drag 移動 / × / ESC / operate-as-you-go) **だけ**。**ポップアップの中身は
//      共有 `presentation/color/ColorPanel` (Modern 流のチャンネルバー UI) に統一**する。
//
// 本ファイルに残すのは **floating 枠** (= title bar drag / × / ESC / position:fixed /
// resize clamp) のみ。中身 (ホイール + 明度 + プレビュー + HEX + RGB/HSV/HSL チャンネル
// バー) は共有 ColorPanel が持つ。旧版にあった @uiw EditableInput 系の数値エディタと
// **パネル内 alpha スライダは撤去**した (= Modern 流に寄せる)。
//
// ## alpha は左ペイン ArgbSliderPanel が担う (退行ではない)
//
// 共有 ColorPanel は alpha 操作 UI を持たない (= Modern 仕様)。Classic では alpha は
// **左ペイン常設の `ArgbSliderPanel` (A/R/G/B スライダの A)** が引き続き担うので、
// Classic は alpha 操作能力を失わない (= ポップアップから消えるだけ)。本コンポーネントの
// `onAlphaChange` prop は public API 互換のため残すが、共有 ColorPanel は alpha を発射
// しないため呼ばれない (= 配線側 ClassicLayout は変更不要)。
//
// ## 設計の核心 (floating 枠側、初版から不変)
//   - **modal でなく floating panel**: title bar drag で動かせる `position: fixed`、
//     背景 backdrop なし。Canvas での描画と並行操作可能。
//   - **明示的に閉じる経路のみ**: × ボタン + ESC キー (= window-level listener)。
//   - controlled: `value` prop が変わるたび内部 (= 共有 ColorPanel) が追従。
//
// ## S3 スライス1 (floating-panels-2026-08-01.md §8): 共通基盤へ寄せる (挙動不変)
//
// drag 移動は S2 で切り出した共有 `usePanelDragGrip` (pointer-capture 配管) +
// `floatShellGeometry` (座標計算/clamp) を使う。旧実装は title bar の
// `onPointerDown` が `preventDefault()` を呼んでおらず、ネイティブ drag /
// テキスト選択が漏れる潜在バグを抱えていた (`FloatShell.tsx` 冒頭コメント参照、
// Modern で実際に踏んだのと同型)。共有 hook に寄せることで自動的に修正される。
// clamp margin は旧 `clampPopupPosition` の既定 (8px) をそのまま
// `clampFloatPosition` へ明示的に渡し、挙動を 1px も変えない (= S3 スライス1は
// リファクタのみ、フロート機構そのものの追加は S3 スライス2)。
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { ColorPanel } from '../../color/ColorPanel.js';
import {
  clampFloatPosition,
  type FloatPoint,
  floatPositionFromPointer,
} from '../shared/floatShellGeometry.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import { outsetFrame } from './legacyFrames.js';

/** 旧 `clampPopupPosition` の既定 margin (8px) と揃える (= 挙動不変)。 */
const POPUP_EDGE_INSET_PX = 8;

/** パネル概ね幅 (= 初期位置算出 + ref 未取得時の clamp fallback). */
const PANEL_WIDTH = 280;
/** ref 未取得時の clamp fallback 高さ (= panel 目安 height). */
const PANEL_HEIGHT_FALLBACK = 440;

export interface ColorWheelPaletteProps {
  /**
   * 描画色 (controlled). 外部 (= ClassicLayout の `pen.color` / `pen.alpha`) が
   * 変わるたびにパネル内部が追従する.
   */
  readonly value: { readonly rgb24: number; readonly alpha255: number };
  /**
   * 色変更時に呼ばれる. wheel / shade slider / HEX / 数値編集のいずれかを弄るたびに
   * 即発火する (= operate-as-you-go). ClassicLayout 側で pen.color と同経路で扱う.
   */
  readonly onColorChange: (rgb24: number) => void;
  /**
   * alpha 変更コールバック (public API 互換のため保持). 2026-06-20 で中身を共有
   * ColorPanel に寄せた結果、本パネルは **alpha を発射しない** (= Classic の alpha 操作は
   * 左ペイン ArgbSliderPanel が担う). よって本 callback は現状呼ばれないが、呼び出し側
   * (ClassicLayout / 背景色 ■) の props 互換を壊さないため signature は不変に保つ.
   */
  readonly onAlphaChange: (alpha255: number) => void;
  /** × ボタン / ESC で閉じるとき呼ばれる. 描画色は最後に反映した値のまま残る. */
  readonly onClose: () => void;
}

/**
 * Clip Studio / Photoshop 風の floating カラーホイールパレット.
 *
 * - floating 枠 (drag / × / ESC / position:fixed) だけを本コンポーネントが持ち、
 *   中身は共有 `ColorPanel` (Modern 流チャンネルバー UI) に委譲する (consult 2026-06-20).
 * - controlled: `value` prop が変わるたび内部 (共有 ColorPanel) が追従.
 * - 即反映: 色操作のたびに `onColorChange` を呼ぶ.
 * - 描画と並行: backdrop なし, `position: fixed` で canvas と共存.
 */
export const ColorWheelPalette = ({
  value,
  onColorChange,
  // onAlphaChange は public API 互換のため受けるが、共有 ColorPanel は alpha を
  // 発射しないため使わない (= Classic の alpha は ArgbSliderPanel 担当).
  onAlphaChange: _onAlphaChange,
  onClose,
}: ColorWheelPaletteProps): React.ReactNode => {
  // ─── ESC で閉じる. window-level listener (= panel focus 中以外でも効く). ──
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // ─── drag 移動 (S2 共有 usePanelDragGrip + floatShellGeometry を使う) ──────
  //
  // 初期位置: 画面右上 (right: 16px, top: 16px). 内部は left/top の数値で持つので
  // viewport 幅から逆算する. SSR では window が無いので default 24/24 に倒す.
  const [popupPos, setPopupPos] = useState<FloatPoint>(() => {
    if (typeof window === 'undefined') return { x: 24, y: 24 };
    const RIGHT_MARGIN = 16;
    const TOP_MARGIN = 16;
    return {
      x: Math.max(16, window.innerWidth - PANEL_WIDTH - RIGHT_MARGIN),
      y: TOP_MARGIN,
    };
  });
  // root 要素 ref. ドラッグ / resize clamp で実寸 (getBoundingClientRect) を読む.
  const panelRef = useRef<HTMLDivElement | null>(null);

  // 実寸を読む helper. ref 未取得時は定数 fallback (= 初回 render 直後等).
  const measurePanel = useCallback((): { width: number; height: number } => {
    const el = panelRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    return { width: PANEL_WIDTH, height: PANEL_HEIGHT_FALLBACK };
  }, []);

  // 実測 grab-offset (= 掴んだ点と panel 左上の相対距離、S2 §11 と同じ流儀).
  // pointerdown 時点で 1 度だけ計測し、ドラッグ中ずっと保持する.
  const grabOffsetRef = useRef<FloatPoint | null>(null);
  const grip = usePanelDragGrip({
    onDragStart: (offset) => {
      grabOffsetRef.current = offset;
    },
    onDragMove: (clientX, clientY) => {
      const offset = grabOffsetRef.current ?? { x: 0, y: 0 };
      setPopupPos(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    },
    onDragEnd: () => {
      grabOffsetRef.current = null;
    },
    measureGrabOffset: (e) => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  // ─── window resize で画面外に取り残されたら引き戻す ──────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => {
      setPopupPos((cur) =>
        clampFloatPosition(
          cur,
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measurePanel]);

  const titleId = useId();

  return (
    // floating panel: position fixed, no backdrop. drag で動かせる.
    <div
      ref={panelRef}
      role="dialog"
      aria-label="カラーホイール"
      aria-labelledby={titleId}
      className={`fixed z-40 w-[280px] bg-[var(--color-panel)] text-white ${outsetFrame}`}
      style={{ left: popupPos.x, top: popupPos.y }}
    >
      {/* タイトルバー = drag handle. 発言小窓と同じパターン.
          ペンタブ対策: `touchAction: 'none'` で OS の pan / scroll ジェスチャ解釈を
          抑止する (= 解釈されると pointercancel が飛んで drag が途切れる). */}
      <div
        className="flex cursor-move select-none items-center justify-between border-b border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[10px] font-bold text-white"
        style={{ touchAction: 'none' }}
        onPointerDown={grip.onPointerDown}
        onPointerMove={grip.onPointerMove}
        onPointerUp={grip.onPointerUp}
        onPointerCancel={grip.onPointerCancel}
        onLostPointerCapture={grip.onLostPointerCapture}
      >
        <span id={titleId}>カラーホイール</span>
        <button
          type="button"
          aria-label="カラーホイールを閉じる"
          // pointerdown が親 (= title bar) に bubble すると `setPointerCapture`
          // が走って × ボタンへの click event が dispatch されなくなる.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="border border-black bg-white px-2 py-0 text-[10px] font-bold text-black"
        >
          ✕
        </button>
      </div>

      {/* 本体 = 共有 ColorPanel (Modern 流チャンネルバー UI、consult 2026-06-20).
          フロート枠側に既にタイトルバー (「カラーホイール」) があるので、ColorPanel の
          「カラー」見出しは showHeader={false} で抑制し二重見出しを避ける. alpha は
          ColorPanel が操作しない (= Classic は左ペイン ArgbSliderPanel で alpha 調整). */}
      <div className="p-1">
        <ColorPanel value={value} onColorChange={onColorChange} showHeader={false} />
      </div>
    </div>
  );
};
